import { withApollo } from '@apollo/react-hoc';
import React, { Fragment, useContext, useState } from 'react';
import { withRouter } from 'react-router';
import { compose } from 'recompose';
import { ThemeProvider } from 'styled-components';
import darkTheme from 'themes/darkTheme';
import lightTheme from 'themes/lightTheme';
import Icon from 'components/Icon';
import Images from 'images';
import PermissionModal from 'pages/track/PermissionModal/PermissionModal';
import { AppContext } from 'shared/AppContext';
import storage from 'shared/storage';
import GetStripeLogin from '../AdminLayout/gql/get-login';
import { getNavSections } from '../NavLayout/config/getNavSections';
import useAccountSetup from '../NavLayout/hooks/useAccountSetup';
import useActiveSection from '../NavLayout/hooks/useActiveSection';
import MobileDrawer from './components/MobileDrawer';
import {
  GlobalStyle,
  MobileContainer,
  TopBar,
  TopBarLogo,
  TopBarLogoWrap,
  HamburgerButton,
  DrawerOverlay,
  ContentArea,
} from './styled/NavMobileLayout.styled';

const platform = process.env.REACT_APP_PLATFORM;

const getTopBarLogo = () => {
  if (platform === 'tickethoss') return Images.logoTickethossHorizontal;
  if (platform === 'kartpass') return Images.logoKart;
  return Images.logoHorizontalWhite;
};

const NavMobileLayout = ({
  children,
  getStripeLogin,
  backgroundColor,
  history,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const { state, dispatch } = useContext(AppContext);

  const {
    user,
    account,
    capabilities,
    accountOptions,
    showPermissionModal,
    setShowPermissionModal,
    setSelectedAccount,
  } = useAccountSetup();

  const sections = getNavSections(user?.me?.role, capabilities);
  const activeSection = useActiveSection(sections);

  const handleThemeToggle = () => {
    const newTheme = state.theme === 'light' ? 'dark' : 'light';
    dispatch({ type: 'SET_THEME', theme: newTheme });
  };

  const handleSettingsClick = () => {
    if (user?.me?.role === 'admin') {
      history.push('/admin/settings');
    } else if (user?.me?.role === 'track') {
      history.push('/admin-track/settings/0');
    } else {
      history.push('/admin-employee/settings/0');
    }
  };

  const handlePermissionDenied = () => {
    setShowPermissionModal(true);
  };

  const handleStripeClick = async () => {
    const result = await getStripeLogin();
    if (result && result.data && result.data.getStripeLogin) {
      window.location.assign(result.data.getStripeLogin);
    } else {
      history.push({
        pathname: '/stripe',
        search: '',
        state: {
          email: storage.get('user').email,
          id: storage.get('user').id,
        },
      });
    }
  };

  const handleAccountChange = (value) => {
    setSelectedAccount(value);
    storage.set('selected_account', value);
  };

  const handleNavigate = (link) => {
    if (link) history.push(link);
  };

  const showAccountSelector =
    user?.me?.role !== 'admin' && user?.me?.accounts?.length > 1;

  if (!user) return null;

  const navTheme = state.theme === 'light' ? lightTheme : darkTheme;

  return (
    <Fragment>
      <MobileContainer>
        <TopBar>
          <HamburgerButton
            onClick={() => setIsVisible(!isVisible)}
            role="button"
            tabIndex={0}
          >
            <Icon icon="Menu-Expand" size={24} color="white" />
          </HamburgerButton>
          <TopBarLogoWrap>
            <TopBarLogo
              src={getTopBarLogo()}
              alt={platform}
              style={platform === 'tickethoss' ? { width: 100 } : {}}
            />
          </TopBarLogoWrap>
        </TopBar>

        <DrawerOverlay
          isVisible={isVisible}
          onClick={() => setIsVisible(false)}
        />

        <ThemeProvider theme={navTheme}>
          <MobileDrawer
            isVisible={isVisible}
            sections={sections}
            activeSection={activeSection}
            onClose={() => setIsVisible(false)}
            onNavigate={handleNavigate}
            showAccountSelector={showAccountSelector}
            account={account}
            accountOptions={accountOptions}
            onAccountChange={handleAccountChange}
            onThemeToggle={handleThemeToggle}
            onSettingsClick={handleSettingsClick}
            onStripeClick={handleStripeClick}
            onPermissionDenied={handlePermissionDenied}
            user={user?.me}
          />
        </ThemeProvider>

        <ContentArea>
          {children}
          <PermissionModal
            isVisible={showPermissionModal}
            hideModal={() => setShowPermissionModal(false)}
            link={
              user?.me?.role === 'track'
                ? '/admin-track/settings/1'
                : '/admin-employee/settings/1'
            }
            history={history}
          />
        </ContentArea>
      </MobileContainer>
      <GlobalStyle backgroundColor={backgroundColor} />
    </Fragment>
  );
};

export default compose(withRouter, withApollo, GetStripeLogin)(NavMobileLayout);
